/**
 * Filter controls for the timeline: search, thread filter, document filter.
 */

import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  OutlinedInput,
  ListSubheader,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import type { ThreadInfo } from "../types";
import { decodeHtmlEntities } from "../utils";

interface TimelineFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showOnlyWithDocuments: boolean;
  onShowOnlyWithDocumentsChange: (show: boolean) => void;
  threadInfo: ThreadInfo[];
  selectedThreadIds: string[];
  onThreadFilterChange: (threadIds: string[]) => void;
}

/**
 * Renders search box, thread filter dropdown, and document filter checkbox.
 */
export function TimelineFilters({
  searchQuery,
  onSearchChange,
  showOnlyWithDocuments,
  onShowOnlyWithDocumentsChange,
  threadInfo,
  selectedThreadIds,
  onThreadFilterChange,
}: TimelineFiltersProps) {
  const handleSelectAllThreads = () => {
    onThreadFilterChange(threadInfo.map((t) => t.threadId));
  };

  const handleClearAllThreads = () => {
    onThreadFilterChange([]);
  };

  return (
    <Box
      sx={{
        mb: 1,
        display: "flex",
        gap: 3,
        alignItems: "flex-start",
        flexWrap: "wrap",
        justifyContent: "space-between",
        pl: 1,
      }}
    >
      {/* Search Box */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "11px",
            fontWeight: "bold",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 0.5,
          }}
        >
          Search keyword
        </Typography>
        <TextField
          size="small"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="standard"
          sx={{
            width: "200px",
            "& .MuiInput-root": {
              fontSize: "0.875rem",
              "&:before": {
                borderBottomColor: "text.disabled",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottomColor: "text.secondary",
              },
              "&:after": {
                borderBottomColor: "primary.main",
              },
            },
            "& .MuiInput-input": {
              padding: "4px 0 5px 0",
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: "text.disabled", fontSize: 18, mr: 0.5 }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Thread Filter Dropdown */}
      {threadInfo.length > 1 && (
        <Box sx={{ minWidth: 250, maxWidth: 350 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "11px",
              fontWeight: "bold",
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.5,
            }}
          >
            Select threads
          </Typography>
          <Select
            multiple
            size="small"
            value={selectedThreadIds}
            onChange={(e) => onThreadFilterChange(e.target.value as string[])}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === threadInfo.length) {
                return "All threads";
              }
              if (selected.length === 0) {
                return "No threads selected";
              }
              return `${selected.length} thread${selected.length > 1 ? "s" : ""} selected`;
            }}
            sx={{
              fontSize: "12px",
              minWidth: 200,
              "& .MuiSelect-select": {
                py: 0.75,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 300 },
              },
            }}
          >
            {/* All / None controls */}
            <ListSubheader
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                py: 0.5,
                lineHeight: 1,
              }}
            >
              <Typography
                component="span"
                onClick={handleSelectAllThreads}
                sx={{
                  fontSize: "11px",
                  color: "primary.main",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                All
              </Typography>
              <Typography
                component="span"
                onClick={handleClearAllThreads}
                sx={{
                  fontSize: "11px",
                  color: "primary.main",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                None
              </Typography>
            </ListSubheader>
            {/* Thread Options */}
            {threadInfo.map((thread) => (
              <MenuItem
                key={thread.threadId}
                value={thread.threadId}
                sx={{ fontSize: "12px" }}
              >
                <Checkbox
                  checked={selectedThreadIds.includes(thread.threadId)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 200,
                        }}
                      >
                        {decodeHtmlEntities(thread.subject)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "10px",
                          color: "text.secondary",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        ({thread.count} <EmailIcon sx={{ fontSize: 10 }} />)
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* Documents Exchanged Filter */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "11px",
            fontWeight: "bold",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            textAlign: "left",
            mb: 0.5,
          }}
        >
          Documents exchanged
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showOnlyWithDocuments}
              onChange={(e) => onShowOnlyWithDocumentsChange(e.target.checked)}
              sx={{
                padding: "2px",
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                },
              }}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                color: "text.secondary",
              }}
            >
              Only with documents
            </Typography>
          }
          sx={{
            margin: 0,
            "& .MuiFormControlLabel-label": {
              marginLeft: "4px",
            },
          }}
        />
      </Box>
    </Box>
  );
}
